/** @format */

import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    TextField,
    MenuItem,
    Alert,
    InputAdornment,
    IconButton,
    Box,
    Typography,
    CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import { AppDispatch, RootState } from "../../../redux/store/store";
import { registerBusinessAdmin } from "../../../redux/slices/businessAuthSlice";
import {
    fetchSupportedCountries,
    ISupportedCountry,
} from "../../../redux/services/businessAuthService";
import { CollectPrimaryButton } from "../styles";
import { colors } from "../../../utility/types/constant";
import { BusinessAuthShell } from "./BusinessAuthShell";

function detectBrowserTimezone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
        return "UTC";
    }
}

const BusinessSignUp: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { isRegistering, registerError } = useSelector(
        (s: RootState) => s.businessAuth,
    );

    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        businessName: "",
        email: "",
        password: "",
        phone: "",
        country: "",
    });
    const [showPassword, setShowPassword] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);
    const [countries, setCountries] = useState<ISupportedCountry[]>([]);
    const [countriesLoading, setCountriesLoading] = useState(true);
    const [countriesError, setCountriesError] = useState<string | null>(null);

    useEffect(() => {
        let active = true;
        setCountriesLoading(true);
        setCountriesError(null);
        fetchSupportedCountries()
            .then((list) => {
                if (!active) return;
                setCountries(list);
                if (!list.length) {
                    setCountriesError(
                        "No countries are available right now. Please try again shortly.",
                    );
                }
            })
            .catch(() => {
                if (active) {
                    setCountriesError(
                        "Could not load countries. Please refresh and try again.",
                    );
                }
            })
            .finally(() => {
                if (active) setCountriesLoading(false);
            });
        return () => {
            active = false;
        };
    }, []);

    const update =
        (field: keyof typeof form) =>
            (e: React.ChangeEvent<HTMLInputElement>) =>
                setForm((f) => ({ ...f, [field]: e.target.value }));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLocalError(null);

        if (!form.country) {
            setLocalError("Please select your country.");
            return;
        }

        if (form.password.length < 8) {
            setLocalError("Password must be at least 8 characters.");
            return;
        }

        const result = await dispatch(
            registerBusinessAdmin({
                firstName: form.firstName.trim(),
                lastName: form.lastName.trim(),
                businessName: form.businessName.trim(),
                email: form.email.trim().toLowerCase(),
                password: form.password,
                phone: form.phone.trim() || undefined,
                country: form.country,
                timezone: detectBrowserTimezone(),
            }),
        );

        if (registerBusinessAdmin.fulfilled.match(result)) {
            navigate("/collect/verify");
        }
    };

    return (
        <BusinessAuthShell
            title="Create your business account"
            subtitle="Set up your workspace to start recovering overdue invoices."
            footer={
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Already have an account?{" "}
                    <Link
                        to="/collect/sign-in"
                        style={{ color: colors.primary, fontWeight: 700 }}
                    >
                        Sign in
                    </Link>
                </Typography>
            }
        >
            <form onSubmit={handleSubmit} noValidate>
                {(localError || registerError) && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {localError || registerError}
                    </Alert>
                )}

                <Box sx={{ display: "flex", gap: 1.5 }}>
                    <TextField
                        label="First name"
                        value={form.firstName}
                        onChange={update("firstName")}
                        fullWidth
                        required
                        size="small"
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Last name"
                        value={form.lastName}
                        onChange={update("lastName")}
                        fullWidth
                        required
                        size="small"
                        margin="dense"
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>

                <TextField
                    label="Business name"
                    value={form.businessName}
                    onChange={update("businessName")}
                    fullWidth
                    required
                    size="small"
                    margin="dense"
                    InputLabelProps={{ shrink: true }}
                />

                <TextField
                    select
                    label="Country"
                    value={form.country}
                    onChange={update("country")}
                    fullWidth
                    required
                    size="small"
                    margin="dense"
                    disabled={countriesLoading || !!countriesError}
                    error={!!countriesError}
                    InputLabelProps={{ shrink: true }}
                    helperText={
                        countriesLoading
                            ? "Loading countries…"
                            : countriesError || "Sets your billing currency."
                    }
                >
                    {countries.map((c) => (
                        <MenuItem key={c.code} value={c.code}>
                            {c.name} ({c.currencySymbol} {c.currency.toUpperCase()})
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    label="Work email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    fullWidth
                    required
                    size="small"
                    margin="dense"
                    InputLabelProps={{ shrink: true }}
                />

                <TextField
                    label="Phone (optional)"
                    value={form.phone}
                    onChange={update("phone")}
                    fullWidth
                    size="small"
                    margin="dense"
                    InputLabelProps={{ shrink: true }}
                />

                <TextField
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={update("password")}
                    fullWidth
                    required
                    size="small"
                    margin="dense"
                    helperText="At least 8 characters."
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowPassword((v) => !v)}
                                    edge="end"
                                >
                                    {showPassword ? (
                                        <VisibilityOff />
                                    ) : (
                                        <Visibility />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <CollectPrimaryButton
                    type="submit"
                    fullWidth
                    disabled={isRegistering}
                    sx={{ mt: 2 }}
                >
                    {isRegistering ? (
                        <CircularProgress size={22} sx={{ color: "#fff" }} />
                    ) : (
                        "Create account"
                    )}
                </CollectPrimaryButton>
            </form>
        </BusinessAuthShell>
    );
};

export default BusinessSignUp;
